import React, { useState, useEffect } from 'react';
import { Calendar, MuiPickersUtilsProvider } from '@material-ui/pickers';

import enAULocale from 'date-fns/locale/en-AU';
import DateFnsUtils from '@date-io/date-fns';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, IconButton, ListItemIcon, MenuItem, Popover, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Icon from '@material-ui/core/Icon';
import ListItemText from '@material-ui/core/ListItemText';
import clsx from 'clsx';
import { startOfYear, endOfYear, format, set } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DEFAULT_DATE_FORMAT, DEFAULT_SHORT_DATE_FORMAT } from 'app/constants/DateTimeFormat';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from 'app/utils';
import AddCalendar from './AddCalendar';
import { DASHBOARD_CARD_STYLE, SCHEDULE_TAB_TYPE } from '../../constants';
import * as DashboardActions from '../../store/actions';

const useStyles = makeStyles(() => ({
  shadow: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
    ...DASHBOARD_CARD_STYLE,
  },
  todayHeader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    flexShrink: 0,
    padding: '8px 8px 4px 8px',
    '& .MuiButton-root': {
      padding: '4px 8px',
      minWidth: 0,
      minHeight: 0,
    },
  },
  todayTitle: {
    fontWeight: 500,
    color: '#0B9FD9',
  },
  dateBar: {
    backgroundColor: '#0b9fd9',
    width: '100%',
    flexShrink: 0,
    padding: 0,
    boxSizing: 'border-box',
    borderLeft: '1px solid #9E9E9E',
    borderRight: '1px solid #9E9E9E',
    '& .MuiButton-root': {
      padding: '4px 16px',
      minWidth: 0,
      minHeight: 0,
    },
  },
  dateBarTitle: {
    fontWeight: 500,
    color: '#ffffff',
  },
  addButtonRow: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '0 8px 8px',
  },
  addIcon: {
    backgroundColor: '#0093cc',
    color: '#ffffff',
    '&.MuiIconButton-root:hover': {
      backgroundColor: '#0093cc',
    },
  },
  item: {
    '& .MuiPopover-paper': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'self-end',
      '& .MuiMenuItem-root': {
        width: '100%',
        justifyContent: 'center',
        gap: '8px',
      },
    },
    '& .MuiListItemIcon-root': {
      marginLeft: '5px',
    },
  },
  centerText: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  pickerDay: {
    '& .MuiPickersCalendarHeader-switchHeader': {
      cursor: 'default',
      flexShrink: 0,
      margin: '2px 0',
    },
    '& .MuiPickersCalendarHeader-daysHeader, & .MuiPickersCalendar-week': {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
      columnGap: 2,
      width: '100%',
      boxSizing: 'border-box',
    },
    '& .MuiPickersCalendarHeader-daysHeader': {
      cursor: 'default',
      flexShrink: 0,
      maxHeight: 'none',
      minHeight: 32,
      justifyContent: 'unset',
      paddingRight: 4,
    },
    '& .MuiPickersCalendarHeader-dayLabel': {
      width: '100%',
      height: 32,
      margin: 0,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: 0,
      color: 'rgba(0, 0, 0, 0.75)',
    },
    '& .MuiPickersCalendar-transitionContainer': {
      width: '100%',
      flex: '1 1 auto',
      minHeight: 0,
      marginTop: 4,
      overflowX: 'hidden',
      overflowY: 'auto',
      scrollbarGutter: 'stable',
      scrollbarWidth: 'thin',
      scrollbarColor: '#BFE3F3 transparent',
      '&::-webkit-scrollbar': {
        width: 6,
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#BFE3F3',
        borderRadius: 4,
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#0B9FD9',
      },
      '& > *:only-child': {
        position: 'relative',
      },
    },
    '& .MuiPickersCalendar-week': {
      minHeight: 32,
      margin: 0,
    },
    '& .MuiPickersCalendar-week > div': {
      width: '100%',
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .MuiPickersDay-day': {
      color: '#000000de',
      width: 32,
      height: 32,
      minWidth: 32,
      minHeight: 32,
      margin: 0,
      padding: 0,
      fontSize: '0.5rem',
      fontWeight: 500,
    },
  },
  dayWithDotContainer: {
    position: 'relative',
  },
  dayWithDot: {
    position: 'absolute',
    height: 0,
    width: 0,
    border: '2px solid',
    borderRadius: 4,
    borderColor: '#0093cc',
    right: '50%',
    transform: 'translateX(1px)',
    top: '80%',
  },
  dayWithDotSelected: {
    position: 'absolute',
    height: 0,
    width: 0,
    border: '2px solid',
    borderRadius: 4,
    borderColor: '#ffffff',
    right: '50%',
    transform: 'translateX(1px)',
    top: '80%',
  },
  calendar: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
    padding: '0 4px',
    boxSizing: 'border-box',
  },
}));

const MyCalendar = (props) => {
  const classes = useStyles(props);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [range, setRange] = useState({
    startDate: startOfYear(new Date()),
    endDate: endOfYear(new Date()),
  });

  const [addTodo, setAddTodo] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [menuItem, setMenuItem] = useState();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { data } = useSelector((state) => state.dashboard.calendar);
  const location = useLocation();

  useEffect(() => {
    setSelectedDate(new Date());
    dispatch(DashboardActions.getCalendarScheduleList(range));
    dispatch(DashboardActions.getCalendarSelectedDate(new Date()));
  }, [location]);

  useEffect(() => {
    dispatch(DashboardActions.getCalendarScheduleList(range));
  }, [range]);

  // useEffect(() => {
  //   dispatch(DashboardActions.getCalendarSelectedDate(selectedDate));
  // }, [selectedDate]);

  const handleMonthChange = (date) => {
    if (date > range.endDate) {
      // next year
      setRange({
        startDate: startOfYear(date),
        endDate: endOfYear(date),
      });
    } else if (date < range.startDate) {
      // previous year
      setRange({
        startDate: startOfYear(date),
        endDate: endOfYear(date),
      });
    }
  };

  const setMidleDay = (date) => {
    return set(date, { hours: 12 });
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    dispatch(DashboardActions.getCalendarSelectedDate(date));
  };

  const handleAddTodo = (event) => {
    setAddTodo(event.currentTarget);
  };

  const addTodoClose = () => {
    setAddTodo(null);
  };

  const handleReturnToday = () => {
    setSelectedDate(new Date());
    dispatch(DashboardActions.getCalendarSelectedDate(new Date()));
  };

  const handleCloseDialog = (item) => {
    setMenuItem(item?.value);
    setOpenDialog(!openDialog);
    setAddTodo(null);
  };

  const handleAddCalendar = (scheduleData) => {
    const _data = {
      title: scheduleData.title,
      colour: scheduleData?.colour,
      detail: scheduleData?.detail,
      isDone: scheduleData?.isDone,
      isEnableRepeat: scheduleData?.isEnableRepeat,
      type: menuItem,
      scheduleDate: setMidleDay(selectedDate),
      repeatType: scheduleData?.isEnableRepeat ? scheduleData?.repeatType : 0,
      repeatInterval: scheduleData?.isEnableRepeat ? scheduleData?.repeatInterval : 0,
      repeatEndDate: scheduleData?.isEnableRepeat ? scheduleData?.repeatEndDate : null,
    };
    dispatch(DashboardActions.createCalendar(_data));
    setOpenDialog(false);
  };

  const renderDayInPicker = (date, _selectedDate, dayInCurrentMonth, dayComponent) => {
    const days = data.map((item) => formatDate(item.scheduleDate, DEFAULT_SHORT_DATE_FORMAT));
    const isSelected = formatDate(date, DEFAULT_DATE_FORMAT) === formatDate(_selectedDate, DEFAULT_DATE_FORMAT);

    if (days?.includes(formatDate(date, DEFAULT_SHORT_DATE_FORMAT)) && dayInCurrentMonth) {
      return (
        <div className={clsx(classes.dayWithDotContainer)}>
          {dayComponent}
          <div className={clsx(isSelected ? classes.dayWithDotSelected : classes.dayWithDot)} />
        </div>
      );
    }

    return dayComponent;
  };

  return (
    <div className={clsx(classes.shadow)}>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={enAULocale}>
        <Box className={clsx(classes.todayHeader)}>
          <Button onClick={() => handleReturnToday()}>
            <Typography className={clsx(classes.todayTitle)} variant="h4">
              {t('dashboard:CALENDAR_BUTTON_TODAY')}
            </Typography>
          </Button>
        </Box>
        <Box className={clsx(classes.dateBar)}>
          <Button>
            <Typography className={clsx(classes.dateBarTitle)} variant="h6">
              {format(selectedDate, 'E, LLL d')}
            </Typography>
          </Button>
        </Box>
        <Box className={clsx(classes.calendar, classes.pickerDay)}>
          <Calendar
            date={selectedDate}
            onChange={handleDateChange}
            onMonthChange={handleMonthChange}
            renderDay={renderDayInPicker}
            allowKeyboardControl={false}
          />
        </Box>
        <Box className={clsx(classes.addButtonRow)}>
          <IconButton size="small" className={clsx(classes.addIcon, 'p-4')} onClick={handleAddTodo}>
            <AddIcon style={{ fontSize: '1.5em', fontWeight: 'lighter' }} />
          </IconButton>
        </Box>
        <Popover
          open={Boolean(addTodo)}
          anchorEl={addTodo}
          onClose={addTodoClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          classes={{
            paper: 'py-8',
          }}
          className={clsx(classes.item)}
          disablePortal={false}
        >
          <MenuItem
            role="button"
            key={SCHEDULE_TAB_TYPE[0].value}
            onClick={() => handleCloseDialog(SCHEDULE_TAB_TYPE[0])}
          >
            <ListItemText
              primary={SCHEDULE_TAB_TYPE[0].label}
              classes={{ primary: 'text-14 list-item-text-primary' }}
              className={clsx(classes.centerText)}
            />
            <ListItemIcon className="min-w-40">
              <Icon fontSize="default">{SCHEDULE_TAB_TYPE[0].icon}</Icon>
            </ListItemIcon>
          </MenuItem>
          <MenuItem
            role="button"
            key={SCHEDULE_TAB_TYPE[2].value}
            onClick={() => handleCloseDialog(SCHEDULE_TAB_TYPE[2])}
          >
            <ListItemText
              primary={SCHEDULE_TAB_TYPE[2].label}
              classes={{ primary: 'text-14 list-item-text-primary basis-full' }}
              className={clsx(classes.centerText)}
            />
            <ListItemIcon className="min-w-40">
              <Icon fontSize="default">{SCHEDULE_TAB_TYPE[2].icon}</Icon>
            </ListItemIcon>
          </MenuItem>
          <MenuItem
            role="button"
            key={SCHEDULE_TAB_TYPE[1].value}
            onClick={() => handleCloseDialog(SCHEDULE_TAB_TYPE[1])}
          >
            <ListItemText
              primary={SCHEDULE_TAB_TYPE[1].label}
              classes={{ primary: 'text-14 list-item-text-primary basis-full' }}
              className={clsx(classes.centerText)}
            />
            <ListItemIcon className="min-w-40">
              <Icon fontSize="default">{SCHEDULE_TAB_TYPE[1].icon}</Icon>
            </ListItemIcon>
          </MenuItem>
        </Popover>
        <AddCalendar
          open={openDialog}
          scheduleType={menuItem}
          onCloseDialog={handleCloseDialog}
          handleAddCalendar={handleAddCalendar}
          date={selectedDate}
        />
      </MuiPickersUtilsProvider>
    </div>
  );
};

export default MyCalendar;
